import { prepareOpenAiCartAttributes } from "@/lib/openaiConsentBridge";
import { CART_RECOVERY_SOURCE_ENABLED, emitRecoveryMutation, recoveryFingerprint, recoveryIdentityMatches, getRecoveryEligibilityGeneration } from "@/lib/cartRecovery";
import { getAuthSnapshot } from "@/lib/auth-snapshot";
import { cancelRecoveryBeforeCheckout } from "@/lib/cartRecoveryCheckout";
import { captureCheckoutIdentity } from "@/lib/cartCheckoutIdentity";
import { metaLazy } from "@/lib/metaLazy";
import { quiet } from "@/lib/quiet-import";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { safeStorage } from "@/lib/browser-storage";
import { toast } from "@/lib/toast";
import {
  CountryCode,
  ShopifyProduct,
  fetchProductByHandle,
  storefrontApiRequest,
} from "@/lib/shopify";
import { DICTS, type Locale } from "@/lib/i18n";
import { getPublicBehaviorSettings } from "@/utils/publicSettings.functions";
import {
  GOOGLE_OWNED_CART_ATTR_KEYS,
  buildGoogleCartAttributes,
} from "@/lib/googleCartAttrs";

import { buildMetaClickAttributes } from "@/lib/metaClickId";
import { buildSnapClickAttributes, getSnapClickId, SNAP_CLICK_ATTR_KEYS } from "@/lib/snapClickId";
import { META_CLICK_ATTR_KEYS } from "@/lib/metaClickKeys";
import { mergeCheckoutTrackingAttributes, safeBuildMeta } from "@/lib/checkoutTrackingAttributes";

import { buildVisitorAttributes } from "@/lib/activityTracker";
import { MARKETS, canCheckoutFromMarket, isGiftDestinationActive, marketFromCountryCode } from "@/lib/markets";
import { isAddedShippingCountry, matchesAddedCountryPricing } from "@/lib/international-shipping";
import { isGiftContactComplete } from "@/lib/gift-phone";
import { readAmbassadorReferral } from "@/lib/ambassadorReferral";
import { reconcileContextCart, type ContextMoney } from "@/lib/storefront-cart-context";

/**
 * Zustand store يقع خارج شجرة React → لا يستطيع استدعاء useLocale().
 * لذا نقرأ اللغة المحفوظة من localStorage (نفس المفتاح الذي يستخدمه LocaleContext)
 * لنعرض رسائل toast مترجمة بلغة المستخدم لا بالعربية الافتراضيّة.
 */
/** Shopify LanguageCode for checkout — only AR/EN are published in every market. */
export function checkoutLanguage(): "AR" | "EN" {
  return getActiveLocale() === "ar" ? "AR" : "EN";
}

function getActiveLocale(): Locale {
  if (typeof window === "undefined") return "en";
  try {
    const raw = localStorage.getItem("lux-locale-v3");
    if (!raw) return "en";
    const parsed = JSON.parse(raw) as { locale?: Locale };
    return parsed.locale ?? "en";
  } catch {
    return "en";
  }
}

/** Localised market name for message placeholders ({current}/{target}). */
function marketLabel(code: string | null | undefined): string {
  if (!code) return "";
  const m = MARKETS[marketFromCountryCode(code)];
  return m.labels[getActiveLocale() === "ar" ? "ar" : "en"];
}

function tStore(key: string, vars?: Record<string, string>): string {
  const locale = getActiveLocale();
  let str = DICTS[locale]?.[key] ?? DICTS.en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      str = str.replace(new RegExp(`\\{${k}\\}`, "g"), v);
    }
  }
  return str;
}

/** عنوان توصيل المُهدى إليه — يُجمع داخل بطاقة السلة ويُرسل لـ Shopify checkout مُعبّأً مسبقاً. */
export interface RecipientAddress {
  /** ISO-2 country code من src/lib/markets.ts */
  country: string;
  /** المدينة / الإمارة */
  city: string;
  /** الشارع والمبنى */
  address1: string;
  /** شقة / طابق (اختياري) */
  address2?: string;
  /** الرقم البريدي (اختياري) */
  zip?: string;
  /** ملاحظات للسائق (اختياري) */
  driverNotes?: string;
}

export interface GiftDetails {
  message?: string;
  /** المناسبة (birthday/anniversary/...) — تُعرض في لوحة Shopify */
  occasion?: string;
  recipientName?: string;
  senderName?: string;
  deliveryDate?: string;
  /** رقم المُهدى إليه بصيغة E.164 — إلزامي عند تفعيل الإشعار التلقائي */
  recipientPhone?: string;
  /** رقم المُهدي بصيغة E.164 — لإشعاره عند تسليم الهديّة */
  senderPhone?: string;
  /** هل يريد المُهدي إخبار المُهدى إليه قبل الوصول (إذا false → مفاجأة كاملة) */
  notifyRecipient?: boolean;
  /** ملاحظة داخلية من العميل إلى الشركة — لا تُطبع على البطاقة ولا تُرسل للمُهدى إليه */
  internalNotes?: string;
  /** لغة رسائل Twilio (ar/en/fr) */
  locale?: "ar" | "en" | "fr";
  /** عنوان توصيل المُهدى إليه — يُملأ inline في السلة، يُسطّح في cart attributes ويُرسل كـ buyer identity */
  recipientAddress?: RecipientAddress;
}

export interface CartItem {
  lineId: string | null;
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: { amount: string; currencyCode: string };
  quantity: number;
  selectedOptions: Array<{ name: string; value: string }>;
  // ℹ️ الإهداء سمة للسلّة كاملة (cartGift) لا للسطر — المعيار العالميّ.
  /** هذا السطر هو عيّنة مجّانيّة من برنامج «هديّة مع الطلب» — سعره 0 وغير قابل للتعديل. */
  isSampleGift?: boolean;
  /** معرّف برنامج الهديّة في DB (gift_programs.id) — لتسجيل الاستلام في webhook. */
  programId?: string;
  /** معرّف العيّنة في DB (gift_program_samples.id) — للإحصاءات. */
  sampleId?: string;
  /** اسم العيّنة المُعرَّف في لوحة Admin (مستقلّ عن اسم Shopify). */
  sampleDisplayName?: string;
  /** صورة مخصّصة للعيّنة من لوحة Admin (مستقلّة عن صورة Shopify). */
  sampleImageUrl?: string | null;
  /** تركيز العطر للعيّنة (EDP/EDT/PARFUM…) — من DB مباشرة، لا regex. */
  sampleConcentration?: string | null;
  /** حجم العيّنة بالمل — من DB مباشرة، لا regex. */
  sampleSizeMl?: number | null;
}

export type MarketSwitchResult =
  | { ok: true }
  | { ok: false; reason: "unavailable" | "failed" | "stale" | "uncertain"; titles?: string[] };

interface CartStore {
  items: CartItem[];
  contextualSubtotal: ContextMoney | null;
  contextualMoneyItems: CartItem[] | null;
  shippingQuote: ContextMoney | null;
  cartId: string | null;
  checkoutUrl: string | null;
  /** Country the cart was created with — when market changes we reset. */
  cartCountry: CountryCode | null;
  /**
   * الإهداء على مستوى السلّة كاملة (المعيار العالميّ): مستلم واحد + عنوان واحد + بطاقة واحدة.
   * null = طلب عاديّ.
   */
  cartGift: GiftDetails | null;
  /** كود خصم/كوبون مُدخل في السلّة — يُلحق تلقائياً بـ checkout URL. */
  discountCode: string | null;
  /**
   * وقت آخر انتقال للـ checkout. إن كانت سلّة Shopify مفقودة بعده نعتبر الطلب أُكمل
   * فنُفرغ السلّة؛ وإن كانت مفقودة بدونه فهي انتهت صلاحيّتها ونعيد بناءها.
   */
  checkoutStartedAt: number | null;

  isLoading: boolean;
  isSyncing: boolean;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  /** يُرجع true عند نجاح الإضافة فعلاً في Shopify، false عند الفشل (مع toast خطأ). */
  addItem: (item: Omit<CartItem, "lineId">, country: CountryCode, recoveryActivity?: boolean,
    restoreBinding?: { isCurrent: () => boolean; validate: () => Promise<boolean>; prepare?: () => boolean }) => Promise<boolean>;
  /** يُحدّث كميّة سطر بعينه. */
  updateQuantity: (lineId: string, quantity: number) => Promise<void>;
  /** يحذف سطراً بعينه. */
  removeItem: (lineId: string) => Promise<void>;
  /** يُفعّل/يُحدّث إهداء السلّة كاملة ويُزامنه مع Shopify (cart attributes + buyer identity). */
  /** true فقط عندما يؤكّد Shopify سمات الهديّة المحفوظة فعلاً (fail closed). */
  setCartGift: (gift: GiftDetails) => Promise<boolean>;
  /** يُلغي الإهداء عن السلّة كاملة؛ true عند تأكيد Shopify لمحو الحقول. */
  clearCartGift: () => Promise<boolean>;
  /**
   * ينقل سلّة قائمة إلى سوق المستلم بعد تأكيد صريح: يتحقّق من توفّر كلّ سطر
   * وسعره في سياق الوجهة من Shopify، يحافظ على الأسطر والكميّات، ولا يُفرغ السلّة.
   */
  switchCartMarketForGift: (target: CountryCode, opts?: { returnToShopperMarket?: boolean }) => Promise<MarketSwitchResult>;
  /**
   * true عندما تعذّر التحقّق من سوق/أسعار سلّة Shopify بعد تبديل فاشل.
   * الدفع محجوب حتى ينجح `recoverCartMarket` أو تُفرَّغ السلّة.
   */
  cartMarketUncertain: boolean;
  /** Persisted checkout address cannot be proven cleared by a country-only update. */
  checkoutContextBlocked: boolean;
  /** يقرأ سلّة Shopify الفعليّة ويعتمد سوقها وأسعارها إن كانت متّسقة مع الأسطر المحليّة. */
  recoverCartMarket: () => Promise<boolean>;
  /** يطبّق كود خصم على سلّة Shopify ويحفظه محلّياً. */
  applyDiscountCode: (code: string) => Promise<void>;
  /** يزيل كود الخصم من حالة السلّة. */
  clearDiscountCode: () => void;

  /** يضيف سطر عيّنة مجّانيّة من برنامج Gift with Purchase — سعر 0، يُضاف كسطر منفصل. */
  addSampleGift: (args: {
    programId: string;
    sampleId: string;
    shopifyVariantId: string;
    displayName: string;
    imageUrl: string | null;
    /** تركيز العطر (اختياريّ) — يُعرض في السلّة بصيغة منظَّمة. */
    concentration?: string | null;
    /** حجم العيّنة بالمل (اختياريّ). */
    sizeMl?: number | null;
    /** معرّف المستخدم المسجّل (إن وُجد) — يُمرَّر سرّاً (`_gift_user_id`) للـ webhook لمنع تكرار «الطلب الأوّل». */
    userId?: string | null;
  }) => Promise<void>;
  clearCart: (initiatingUserId?: string | null, eligibilityGeneration?: string | null, initiatingIdentityRevision?: number) => void;
  /**
   * يُزامن مع Shopify فقط عند تغيّر فعليّ في السلّة (بصمة cartId + الأسطر + السوق)
   * أو بعد تجاوز نافذة `SYNC_MIN_INTERVAL_MS`. `force: true` يتخطّى البوّابة.
   */
  syncCart: (options?: { force?: boolean }) => Promise<void>;
  /** Call when active market changes — clears cart if country differs. */
  ensureMarket: (country: CountryCode) => void;
  getCheckoutUrl: () => string | null;
  /** Refresh the cart market context and checkout URL immediately before payment. */
  prepareCheckout: () => Promise<string | null>;
}

const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) { id totalQuantity }
  }
`;

const CART_CHECKOUT_QUERY = `
  query cartCheckout($id: ID!, $country: CountryCode!, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    localization { country { isoCode } }
    cart(id: $id) { id totalQuantity checkoutUrl buyerIdentity { countryCode } cost { subtotalAmount { currencyCode } } attributes { key value } }
  }
`;

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!, $country: CountryCode!, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        lines(first: 100) {
          edges { node { id merchandise { ... on ProductVariant { id } } } }
        }
      }
      userErrors { field message }
    }
  }
`;

const CART_LINES_ADD_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 100) {
          edges { node { id merchandise { ... on ProductVariant { id } } } }
        }
      }
      userErrors { field message }
    }
  }
`;

const CART_LINES_UPDATE_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { id }
      userErrors { field message }
    }
  }
`;

const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { id }
      userErrors { field message }
    }
  }
`;

const CART_DISCOUNT_CODES_UPDATE_MUTATION = `
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart { id }
      userErrors { field message }
    }
  }
`;

/**
 * تحديث سمات السلة على مستوى cart نفسه (لا السطر).
 * نستعملها لوضع علم `is_gift=true` لتقرأه Payment Customization Function في Shopify
 * فتُخفي طريقة COD على Checkout عند الهديّة.
 */
const CART_ATTRIBUTES_UPDATE_MUTATION = `
  mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart { id attributes { key value } }
      userErrors { field message }
    }
  }
`;

export function formatCheckoutUrl(checkoutUrl: string, discountCode?: string | null): string {
  try {
    const url = new URL(checkoutUrl);
    // Shopify قد يُرجع preview_theme_id داخل checkoutUrl؛ نحذفه ليُعرض الثيم الحيّ دائماً.
    url.searchParams.delete("preview_theme_id");
    url.searchParams.set("channel", "online_store");
    if (discountCode?.trim()) {
      url.searchParams.set("discount", discountCode.trim());
    }
    // جسر Snap: نلحق ScCid برابط checkout ليقرأه تطبيق Snapchat في Shopify
    // (أحداث Purchase تُرسَل من هناك، لا من موقعنا). رهن موافقة الإعلانات.
    const snapClick = getSnapClickId();
    if (snapClick) {
      url.searchParams.set("ScCid", snapClick.sccid);
    }
    return url.toString();
  } catch {
    return checkoutUrl;
  }
}

function isCartNotFoundError(
  userErrors: Array<{ field: string[] | null; message: string }>,
): boolean {
  return userErrors.some(
    (e) =>
      e.message.toLowerCase().includes("cart not found") ||
      e.message.toLowerCase().includes("does not exist"),
  );
}

/**
 * سمات الهديّة على مستوى السلّة كاملة (تصل Shopify كـ note_attributes على الطلب).
 * لا توجد سمات هديّة على مستوى السطر بعد الآن — الإهداء سمة للطلب كلّه.
 */
function buildGiftAttributes(gift: GiftDetails | null | undefined) {
  if (!gift) return [];
  const attrs: Array<{ key: string; value: string }> = [];
  const g = gift;
  // ✅ حقول مرئيّة للعميل في صفحة تأكيد الطلب + الإيميل (بلا بادئة _)

  // 🎁 علم ثابت لا يرتبط بأيّ إعداد — مصدر الحقيقة للتمييز بين هديّة وشراء شخصيّ
  // (يظهر في Shopify Admin وZapier وقاعدتنا عبر isGiftFromAttributes).
  attrs.push({ key: "Gift order", value: "Yes" });
  if (g.occasion) attrs.push({ key: "Occasion", value: g.occasion });

  if (g.message) attrs.push({ key: "Gift message", value: g.message });
  if (g.recipientName) attrs.push({ key: "Recipient", value: g.recipientName });
  if (g.senderName) attrs.push({ key: "From", value: g.senderName });
  if (g.deliveryDate) attrs.push({ key: "Preferred delivery", value: g.deliveryDate });
  // 🔒 حقول داخليّة — مخفيّة عن العميل (بادئة _) وتظهر فقط في Admin/Webhook
  if (g.recipientPhone) attrs.push({ key: "_Recipient phone", value: g.recipientPhone });
  if (g.senderPhone) attrs.push({ key: "_Sender phone", value: g.senderPhone });
  if (g.notifyRecipient) attrs.push({ key: "_Notify recipient", value: "yes" });
  if (g.internalNotes) attrs.push({ key: "_Internal note (for Lecmo)", value: g.internalNotes });
  if (g.locale) attrs.push({ key: "_Locale", value: g.locale });
  const a = g.recipientAddress;
  if (a) {
    if (a.country) attrs.push({ key: "_Recipient address — Country", value: a.country });
    if (a.city) attrs.push({ key: "_Recipient address — City", value: a.city });
    if (a.address1) attrs.push({ key: "_Recipient address — Line", value: a.address1 });
    if (a.address2) attrs.push({ key: "_Recipient address — Line 2", value: a.address2 });
    if (a.zip) attrs.push({ key: "_Recipient address — Zip", value: a.zip });
    if (a.driverNotes) attrs.push({ key: "_Recipient address — Driver notes", value: a.driverNotes });
  }
  return attrs;
}

/**
 * تُعتبر بيانات المُهدى إليه «صالحة للإرسال» متى توفّر:
 *   اسم + دولة + مدينة + عنوان تفصيلي. ما دون ذلك نُبقيها draft فقط.
 */
function isShippableGift(g: GiftDetails): boolean {
  const a = g.recipientAddress;
  return !!(g.recipientName?.trim() && a?.country && a.city?.trim() && a.address1?.trim());
}

/** يقرأ كود السفير من cookie لربط الطلب لاحقاً في webhook */
function readAmbassadorRefCookie(): string | null {
  return readAmbassadorReferral();
}

// Cache بسيط للإعداد السلوكيّ "إخفاء COD على الهدايا" — يُجلَب من السيرفر مرّة كلّ ٥ دقائق.
// لو OFF: لا نُرسل سمة is_gift أصلاً، فيظهر COD طبيعيّاً حتى للهدايا.
let _giftHideCodCache: { value: boolean; expires: number } = { value: true, expires: 0 };
const GIFT_HIDE_COD_TTL_MS = 5 * 60_000;

async function shouldFlagGiftForCod(): Promise<boolean> {
  const now = Date.now();
  if (_giftHideCodCache.expires > now) return _giftHideCodCache.value;
  try {
    
    const res = await getPublicBehaviorSettings();
    _giftHideCodCache = { value: !!res.giftHideCodEnabled, expires: now + GIFT_HIDE_COD_TTL_MS };
    return _giftHideCodCache.value;
  } catch {
    // فشل الجلب → افتراضياً ON (السلوك الحاليّ آمن: يُخفي COD على الهدايا)
    return true;
  }
}

/**
 * سمات السلّة كاملة: علم الهديّة (لإخفاء COD) + كود السفير + كلّ تفاصيل الهديّة.
 */
async function buildCartAttributes(
  gift: GiftDetails | null | undefined,
): Promise<Array<{ key: string; value: string }>> {
  const attrs: Array<{ key: string; value: string }> = [];
  // لا نُرسل is_gift إلّا إذا كان السلوك مفعَّلاً ـ هكذا يُسلَّم COD طبيعيّاً عند إيقاف الـtoggle.
  if (gift && (await shouldFlagGiftForCod())) {
    attrs.push({ key: "is_gift", value: "true" });
  }
  const ref = readAmbassadorRefCookie();
  if (ref) attrs.push({ key: "ambassador_ref", value: ref });
  // جوجل: هويّة جلسة GA4 + نقرة Ads + UTM — كلّها رهن الموافقة القائمة.
  attrs.push(...buildGoogleCartAttributes());
  // معرّفات نقرة Meta (fbclid/fbc/fbp) — الجسر الوحيد بين جلسة الموقع وطلب شوبيفاي
  attrs.push(...buildMetaClickAttributes());
  // معرّف نقرة Snap (ScCid) — يصل إلى note_attributes ليقرأه تطبيق Snapchat
  attrs.push(...buildSnapClickAttributes());
  attrs.push(...buildVisitorAttributes());

  attrs.push(...buildGiftAttributes(gift));
  return attrs;
}

async function createShopifyCart(
  item: CartItem,
  country: CountryCode,
  gift: GiftDetails | null | undefined,
) {
  const cartAttributes = await buildCartAttributes(gift);
  const data = await storefrontApiRequest<any>(CART_CREATE_MUTATION, {
    country,
    language: checkoutLanguage(),
    input: {
      buyerIdentity: { countryCode: country },
      // علم على مستوى السلة كلّها — تقرأه Shopify Function لإخفاء COD
      // + كود السفير من cookie لربط الطلب في webhook
      attributes: cartAttributes,
      lines: [
        {
          quantity: item.quantity,
          merchandiseId: item.variantId,
        },
      ],
    },
  }, { cacheTtlMs: 0 });
  if (data?.data?.cartCreate?.userErrors?.length) {
    console.error("Cart creation failed:", data.data.cartCreate.userErrors);
    return null;
  }
  const cart = data?.data?.cartCreate?.cart;
  if (!cart?.checkoutUrl) return null;
  const lineId = cart.lines.edges[0]?.node?.id;
  if (!lineId) return null;
  return { cartId: cart.id, checkoutUrl: formatCheckoutUrl(cart.checkoutUrl), lineId };
}

/** أدنى فاصل زمنيّ لإعادة المزامنة عندما لا تتغيّر بصمة السلّة (١٠ دقائق). */
const SYNC_MIN_INTERVAL_MS = 10 * 60 * 1000;

/** بصمة حالة السلّة — تتغيّر فقط عند تغيّر فعليّ في الأسطر/الكميّات/السوق. */
function cartSignature(state: {
  cartId: string | null;
  cartCountry: CountryCode | null;
  items: CartItem[];
}): string {
  return [
    state.cartId ?? "-",
    state.cartCountry ?? "-",
    state.items
      .map((i) => `${i.variantId}:${i.quantity}:${i.lineId ?? "-"}`)
      .sort()
      .join("|"),
  ].join("::");
}

/** حالة المزامنة الأخيرة — في الذاكرة فقط (لا تُخزَّن) لضمان مزامنة واحدة عند كلّ تحميل. */
let lastSyncSignature: string | null = null;
let lastSyncAt = 0;

/**
 * جاهزيّة كشف السوق (IP + ملفّ العميل) — يضبطها LocaleContext.
 * قبل الجاهزيّة السوق الافتراضيّ AE، فأيّ إضافة مبكّرة كانت تُنشئ سلّة
 * بسوق خاطئ ثمّ تُفرَغ عند وصول الكشف (SA/OM/IQ). نؤجّل الإضافة قليلاً.
 */
let marketReadyFlag = false;
let activeCountryFlag: CountryCode | null = null;
/** يضبطها LocaleContext بالسوق الفعليّ (يشمل سوق الإهداء) وحالة الجاهزيّة. */
export function setCartMarketContext(country: CountryCode | null, ready: boolean) {
  activeCountryFlag = country;
  marketReadyFlag = ready;
}
async function waitForMarketReady(maxMs = 5000) {
  if (marketReadyFlag) return;
  const start = Date.now();
  while (!marketReadyFlag && Date.now() - start < maxMs) {
    await new Promise((r) => setTimeout(r, 120));
  }
}



async function addLineToShopifyCart(cartId: string, item: CartItem) {
  const data = await storefrontApiRequest<any>(CART_LINES_ADD_MUTATION, {
    cartId,
    lines: [
      {
        quantity: item.quantity,
        merchandiseId: item.variantId,
      },
    ],
  }, { cacheTtlMs: 0 });

  const userErrors = data?.data?.cartLinesAdd?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length) {
    console.error("Add line failed:", userErrors);
    return { success: false };
  }
  const lines = data?.data?.cartLinesAdd?.cart?.lines?.edges || [];
  const newLine = lines.find(
    (l: any) => l.node.merchandise.id === item.variantId,
  );
  return { success: true as const, lineId: newLine?.node?.id as string | undefined };
}

async function updateShopifyCartLine(cartId: string, lineId: string, quantity: number) {
  const data = await storefrontApiRequest<any>(CART_LINES_UPDATE_MUTATION, {
    cartId,
    lines: [{ id: lineId, quantity }],
  }, { cacheTtlMs: 0 });
  const userErrors = data?.data?.cartLinesUpdate?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length) {
    console.error("Update line failed:", userErrors);
    return { success: false };
  }
  return { success: true as const };
}

async function removeLineFromShopifyCart(cartId: string, lineId: string) {
  const data = await storefrontApiRequest<any>(CART_LINES_REMOVE_MUTATION, {
    cartId,
    lineIds: [lineId],
  }, { cacheTtlMs: 0 });
  const userErrors = data?.data?.cartLinesRemove?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length) {
    console.error("Remove line failed:", userErrors);
    return { success: false };
  }
  return { success: true as const };
}

/**
 * تُحدّث سمات السلّة (is_gift + كود السفير + تفاصيل الهديّة).
 * Payment Customization Function في Shopify تقرأ is_gift وتُخفي COD عند true.
 */
const CART_ATTRIBUTES_QUERY = `
  query cartAttributes($id: ID!) {
    cart(id: $id) { id attributes { key value } }
  }
`;

type Attr = { key: string; value: string };

/** كلّ المفاتيح التي تملكها الهديّة — تُمحى صراحةً عند إزالتها. */
export const GIFT_OWNED_CART_ATTR_KEYS = [
  "is_gift",
  "Gift order",
  "Occasion",
  "Gift message",
  "Recipient",
  "From",
  "Preferred delivery",
  "_Recipient phone",
  "_Sender phone",
  "_Notify recipient",
  "_Internal note (for Lecmo)",
  "_Locale",
  "_Recipient address — Country",
  "_Recipient address — City",
  "_Recipient address — Line",
  "_Recipient address — Line 2",
  "_Recipient address — Zip",
  "_Recipient address — Driver notes",
];

/** سمات الهديّة المتوقّعة على Shopify (is_gift رهن gift_hide_cod_enabled). */
export async function expectedGiftAttributes(gift: GiftDetails | null | undefined): Promise<Attr[]> {
  if (!gift) return [];
  const attrs: Attr[] = [];
  if (await shouldFlagGiftForCod()) attrs.push({ key: "is_gift", value: "true" });
  attrs.push(...buildGiftAttributes(gift));
  return attrs;
}

/**
 * يدمج سمات الهديّة مع سمات السلّة القائمة: يحافظ على كلّ مفتاح غير مملوك
 * للهديّة (تتبّع/سفير/زائر)، ويكتب قيمة فارغة صريحة لكلّ حقل هديّة سابق أُزيل.
 */
export function mergeGiftCartAttributes(existing: Attr[], expected: Attr[]): Attr[] {
  const owned = new Set(GIFT_OWNED_CART_ATTR_KEYS);
  const out = existing.filter((a) => !owned.has(a.key));
  out.push(...expected);
  const keep = new Set(expected.map((a) => a.key));
  for (const a of existing) {
    if (owned.has(a.key) && a.value && !keep.has(a.key)) out.push({ key: a.key, value: "" });
  }
  return out;
}

/** هل تطابق سمات Shopify الحقول المتوقّعة للهديّة تماماً (الفارغ = غير موجود)؟ */
export function giftAttributesMatch(actual: Attr[], expected: Attr[]): boolean {
  const got = new Map(actual.filter((a) => a.value !== "").map((a) => [a.key, a.value]));
  const want = new Map(expected.map((a) => [a.key, a.value]));
  return GIFT_OWNED_CART_ATTR_KEYS.every((k) => got.get(k) === want.get(k));
}

function isTransientShopifyError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return error instanceof TypeError || /HTTP error: (408|429|5\d\d)/.test(msg);
}

const GIFT_SYNC_MAX_ATTEMPTS = 3;

/**
 * يحفظ سمات الهديّة على سلّة Shopify ويتحقّق من القراءة الراجعة.
 * يعيد false عند أيّ userErrors أو استجابة ناقصة أو عدم تطابق (fail closed)،
 * ويعيد المحاولة فقط لأخطاء النقل العابرة (حدّ ٣ محاولات).
 */
async function syncCartGiftAttribute(cartId: string, gift: GiftDetails | null): Promise<boolean> {
  for (let attempt = 0; attempt < GIFT_SYNC_MAX_ATTEMPTS; attempt++) {
    try {
      const current = await storefrontApiRequest<any>(CART_ATTRIBUTES_QUERY, { id: cartId }, { cacheTtlMs: 0 });
      const cart = current?.data?.cart;
      if (!cart?.id || cart.id !== cartId) return false;
      const existing = (cart.attributes ?? []) as Attr[];
      const expected = await expectedGiftAttributes(gift);
      // الدولة حقل إلزاميّ للهديّة ويجب أن يُحفظ ويُتحقَّق منه في السمات.
      if (gift && !expected.some((a) => a.key === "_Recipient address — Country" && a.value)) return false;
      const attributes = mergeGiftCartAttributes(existing, expected);
      const res = await storefrontApiRequest<any>(CART_ATTRIBUTES_UPDATE_MUTATION, {
        cartId,
        attributes,
      }, { cacheTtlMs: 0 });
      const payload = res?.data?.cartAttributesUpdate;
      if (!payload || (payload.userErrors ?? []).length > 0 || payload.cart?.id !== cartId) {
        console.error("Gift attributes not confirmed by Shopify:", payload?.userErrors);
        return false;
      }
      const saved = (payload.cart.attributes ?? []) as Attr[];
      if (!giftAttributesMatch(saved, expected)) return false;
      // السمات غير المملوكة للهديّة يجب أن تبقى كما كانت.
      const savedMap = new Map(saved.map((a) => [a.key, a.value]));
      const owned = new Set(GIFT_OWNED_CART_ATTR_KEYS);
      return existing.every((a) => owned.has(a.key) || (savedMap.get(a.key) ?? "") === (a.value ?? ""));
    } catch (error) {
      if (attempt < GIFT_SYNC_MAX_ATTEMPTS - 1 && isTransientShopifyError(error)) {
        await new Promise((r) => setTimeout(r, 250 * 2 ** attempt));
        continue;
      }
      console.error("Failed to sync gift cart attributes:", error);
      return false;
    }
  }
  return false;
}

/**
 * قفل واحد لكلّ تعديلات سلّة Shopify (أسطر/سوق/هديّة/دفع): تُنفَّذ بالتتابع
 * فلا تتداخل كتابة قديمة مع أحدث. `holdCartLockUntil` يُبقي القفل حتى تستقرّ
 * كتابة تجاوزت مهلتها، فلا تهبط متأخّرةً فوق نيّة أحدث.
 */
let cartLockTail: Promise<unknown> = Promise.resolve();
/** كتابات Shopify ما زالت معلّقة بعد انتهاء مهمّتها (مهلة تجاوزها المتّصل). */
const heldCartWrites = new Set<Promise<void>>();
/** كلّ مهمّة — حتى المرتبطة بالذيل قبل الإمساك — تنتظر الكتابات المعلّقة عند بدئها. */
async function waitHeldCartWrites() {
  while (heldCartWrites.size > 0) await Promise.all([...heldCartWrites]);
}
export function withCartLock<T>(fn: () => Promise<T>): Promise<T> {
  const run = cartLockTail.then(() => undefined, () => undefined).then(waitHeldCartWrites).then(fn);
  cartLockTail = run.then(() => undefined, () => undefined);
  return run;
}
/** Capture identity BEFORE joining the shared lock tail, never after queued work begins. */
function withRecoveryMutationLock<T>(fn: (initiatingUserId: string | null, eligibilityGeneration: string | null, initiatingIdentityRevision: number) => Promise<T>): Promise<T> {
  const owner = getAuthSnapshot().userId;
  const generation = getRecoveryEligibilityGeneration(owner);
  const revision = getAuthSnapshot().identityRevision;
  return withCartLock(() => fn(owner, generation, revision));
}
function holdCartLockUntil(p: Promise<unknown>) {
  const settled: Promise<void> = p.then(() => undefined, () => undefined).then(() => {
    heldCartWrites.delete(settled);
  });
  heldCartWrites.add(settled);
}
/** للاختبارات فقط. */
export function __resetCartLockForTests() {
  cartLockTail = Promise.resolve();
  heldCartWrites.clear();
  giftIntentSeq = 0;
}

/** آخر نيّة هديّة (حفظ/إزالة) — النيّة الأقدم لا تكتب على Shopify بعد أحدث. */
let giftIntentSeq = 0;

/** بصمة الأسطر المحليّة (السطر + المتغيّر + الكميّة). */
function linesSignature(items: CartItem[]): string {
  return items.map((i) => `${i.lineId ?? "-"}:${i.variantId}:${i.quantity}`).sort().join("|");
}

/**
 * سلّة Shopify متّسقة مع الأسطر المحليّة في `country`؟ نفس الأسطر والمتغيّرات
 * والكميّات، بعملة السوق المتحقَّق منها، وbuyer identity بالدولة نفسها.
 */
function cartCoherentWith(cart: any, cartId: string, items: CartItem[], country: string): boolean {
  if (!cart || cart.id !== cartId || !cart.checkoutUrl) return false;
  if (cart.buyerIdentity?.countryCode !== country) return false;
  if (isAddedShippingCountry(country) && cart.resolvedCountry !== country) return false;
  const currency = MARKETS[marketFromCountryCode(country)].currency;
  const lines = new Map<string, any>((cart.lines?.edges ?? []).map((e: any) => [e.node.id, e.node]));
  const addedCurrency = cart.lines?.edges?.[0]?.node?.cost?.amountPerQuantity?.currencyCode;
  if (isAddedShippingCountry(country) && !matchesAddedCountryPricing(country, cart.resolvedCountry, addedCurrency)) return false;
  return lines.size === items.length && items.every((i) => {
    const l = i.lineId ? lines.get(i.lineId) : null;
    return !!l && l.quantity === i.quantity && l.merchandise?.id === i.variantId &&
      l.cost?.amountPerQuantity?.currencyCode === (isAddedShippingCountry(country) ? addedCurrency : currency);
  });
}

async function readCartInContext(cartId: string, country: string): Promise<any> {
  try {
    const c = await storefrontApiRequest<any>(
      CART_LINES_CONTEXT_QUERY,
      { id: cartId, country, language: checkoutLanguage() },
      { cacheTtlMs: 0 },
    );
    const cart = c?.data?.cart;
    return cart ? { ...cart, resolvedCountry: c?.data?.localization?.country?.isoCode } : null;
  } catch {
    return null;
  }
}

/**
 * سعر متغيّر واحد في سياق دولة المستلم من Shopify (لا نثق بسعر props القديم).
 * null = غير متوفّر/عملة غير متوقّعة/فشل.
 */
export async function fetchVariantPriceInContext(
  variantId: string,
  country: string,
): Promise<{ amount: string; currencyCode: string } | null> {
  try {
    const v = await storefrontApiRequest<any>(
      VARIANTS_IN_CONTEXT_QUERY,
      { ids: [variantId], country, language: checkoutLanguage() },
      { cacheTtlMs: 0 },
    );
    const n = v?.data?.nodes?.[0];
    const currency = MARKETS[marketFromCountryCode(country)].currency;
    if (!n || n.id !== variantId || !n.availableForSale) return null;
    if (isAddedShippingCountry(country)) {
      if (!matchesAddedCountryPricing(country, v?.data?.localization?.country?.isoCode, n.price?.currencyCode)) return null;
    } else if (n.price?.currencyCode !== currency) return null;
    return { amount: String(n.price.amount), currencyCode: n.price.currencyCode };
  } catch {
    return null;
  }
}

const VARIANTS_IN_CONTEXT_QUERY = `
  query variantsInContext($ids: [ID!]!, $country: CountryCode!, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    localization { country { isoCode } }
    nodes(ids: $ids) {
      ... on ProductVariant { id availableForSale price { amount currencyCode } product { title } }
    }
  }
`;

const CART_LINES_CONTEXT_QUERY = `
  query cartLinesInContext($id: ID!, $country: CountryCode!, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    localization { country { isoCode } }
    cart(id: $id) {
      id checkoutUrl totalQuantity buyerIdentity { countryCode }
      cost { subtotalAmount { amount currencyCode } }
      deliveryGroups(first: 100) {
        pageInfo { hasNextPage }
        edges { node { deliveryAddress { countryCodeV2 } selectedDeliveryOption { estimatedCost { amount currencyCode } } } }
      }
      lines(first: 100) { edges { node { id quantity merchandise { ... on ProductVariant { id } } cost { amountPerQuantity { amount currencyCode } } } } }
    }
  }
`;

/**
 * يُرسل عنوان المُهدى إليه إلى Shopify كـ buyer identity / delivery address
 * بحيث يصل العميل إلى صفحة الدفع والعنوان مُعبّأ مسبقاً.
 * الفشل يُتجاهل بصمت — سمات السلّة تكفي لفريقكم يدوياً.
 */
const CART_BUYER_IDENTITY_UPDATE_MUTATION = `
  mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart { id }
      userErrors { field message }
    }
  }
`;

async function syncRecipientAddressToBuyerIdentity(
  cartId: string,
  gift: GiftDetails | null,
) {
  if (!gift || !isShippableGift(gift) || !gift.recipientAddress) return;
  const a = gift.recipientAddress;
  // لا نغيّر سوق السلّة ضمنيّاً عبر عنوان التوصيل — التبديل يمرّ بالتأكيد الصريح فقط.
  if (a.country !== useCartStore.getState().cartCountry) return;
  const name = gift.recipientName?.trim() ?? "";
  const [firstName, ...rest] = name.split(/\s+/);
  const lastName = rest.join(" ");
  try {
    const res = await storefrontApiRequest<any>(CART_BUYER_IDENTITY_UPDATE_MUTATION, {
      cartId,
      buyerIdentity: {
        countryCode: a.country,
        ...(gift.recipientPhone ? { phone: gift.recipientPhone } : {}),
        deliveryAddressPreferences: [
          {
            deliveryAddress: {
              firstName: firstName || name || undefined,
              lastName: lastName || undefined,
              address1: a.address1,
              ...(a.address2 ? { address2: a.address2 } : {}),
              city: a.city,
              ...(a.zip ? { zip: a.zip } : {}),
              country: a.country,
              ...(gift.recipientPhone ? { phone: gift.recipientPhone } : {}),
            },
          },
        ],
      },
    }, { cacheTtlMs: 0 });
    const errs = res?.data?.cartBuyerIdentityUpdate?.userErrors ?? [];
    // تعبئة العنوان مسبقاً في Checkout تحسين فقط؛ مصدر الحقيقة سمات السلّة المتحقَّق منها.
    if (errs.length) console.warn("Recipient address prefill rejected:", errs);
  } catch (error) {
    // تجاهل بصمت — Shopify قد يرفض دولاً معيّنة أو حقولاً ناقصة
    console.warn("Recipient address not synced to buyer identity:", error);
  }
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      contextualSubtotal: null,
      contextualMoneyItems: null,
      shippingQuote: null,
      cartId: null,
      checkoutUrl: null,
      cartCountry: null,
      cartGift: null,
      discountCode: null,
      checkoutStartedAt: null,
      cartMarketUncertain: false,
      checkoutContextBlocked: false,

      isLoading: false,
      isSyncing: false,
      isOpen: false,
      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),

      ensureMarket: (country) => {
        const { cartCountry, items, clearCart } = get();
        if (cartCountry && cartCountry !== country && items.length > 0) {
          clearCart();
          // ممنوع الإفراغ الصامت: العميل كان يرى «سلّتك فارغة» بلا سبب.
          toast.error(
            tStore("cart.market_mismatch", { current: marketLabel(cartCountry), target: marketLabel(country) }),
            { duration: 7000 },
          );
        }
      },


      addItem: (item, requestedCountry, recoveryActivity = true, restoreBinding) => withRecoveryMutationLock(async (initiatingUserId, eligibilityGeneration, initiatingIdentityRevision) => {
        const restoreCurrent = () => !restoreBinding ||
          (restoreBinding.isCurrent() && (initiatingIdentityRevision === getAuthSnapshot().identityRevision && recoveryIdentityMatches(initiatingUserId, getAuthSnapshot().userId)));
        if (!restoreCurrent()) return false;
        if (restoreBinding && !(await restoreBinding.validate())) return false;
        if (!restoreCurrent()) return false;
        const recoveryBefore = CART_RECOVERY_SOURCE_ENABLED ? recoveryFingerprint(get().cartCountry, get().items) : null;
        // لا نبني سلّة Shopify قبل أن يستقرّ السوق الحقيقيّ للعميل.
        await waitForMarketReady();
        if (!restoreCurrent()) return false;
        // GA4-only verified market snapshot: never the requestedCountry fallback.
        const ga4Market: CountryCode | undefined =
          marketReadyFlag && activeCountryFlag !== null ? activeCountryFlag : undefined;
        // بعد الانتظار نستعمل السوق الفعليّ لا القيمة القديمة وقت الكبس.
        const country = activeCountryFlag ?? requestedCountry;
        if (restoreBinding && country !== requestedCountry) return false;
        if (restoreBinding && !(await restoreBinding.validate())) return false;
        if (!restoreCurrent()) return false;
        if (restoreBinding?.prepare && !restoreBinding.prepare()) return false;
        const { items, cartId, cartCountry, clearCart } = get();

        // بقيّة العالم (ROW): لا شحن. نمنع الإضافة مركزيّاً هنا لا في كلّ زرّ،
        // لئلّا يبني الزائر سلّة تفشل عند الدفع. الإهداء يمرّ لأنّ سوق المستلم مدعوم.
        if (!canCheckoutFromMarket(marketFromCountryCode(country))) {
          toast.error(tStore("cart.row_no_shipping"), { duration: 8000 });
          return false;
        }

        // New destinations require an exact Shopify context before any cart write.
        // Prices remain in the API's real currency, never relabelled USD.
        if (isAddedShippingCountry(country)) {
          const price = await fetchVariantPriceInContext(item.variantId, country);
          if (!restoreCurrent()) return false;
          if (!price) {
            toast.error(tStore("cart.destination_unavailable"));
            return false;
          }
          item = { ...item, price };
        }



        // منع خلط الأسواق: إذا السلة فيها عناصر بسوق مختلف، نرفض ونعرض رسالة
        if (cartCountry && cartCountry !== country && items.length > 0) {
          toast.error(
            tStore("cart.market_mismatch", { current: marketLabel(cartCountry), target: marketLabel(country) }),
            { duration: 6000 },
          );
          return false;
        }
        const effectiveCartId = cartCountry === country ? cartId : null;

        const existingItem = items.find((i) => i.variantId === item.variantId && !i.isSampleGift);


        set({ isLoading: true });
        const cartGift = get().cartGift;
        // نجاح فعليّ = Shopify أكّد السطر. الفشل يُبلَّغ للمستخدم بـ toast لا صامتاً.
        let ok = false;
        try {
          if (!effectiveCartId) {
            const result = await createShopifyCart({ ...item, lineId: null }, country, cartGift);
            if (!restoreCurrent()) return false;
            if (result) {
              set({
                cartId: result.cartId,
                checkoutUrl: result.checkoutUrl,
                cartCountry: country,
                items: [{ ...item, lineId: result.lineId }],
              });
              ok = true;
              if (cartGift) {
                await syncRecipientAddressToBuyerIdentity(result.cartId, cartGift);
              }
            }
          } else if (existingItem) {
            const newQuantity = existingItem.quantity + item.quantity;
            if (!existingItem.lineId) {
              set({ isLoading: false });
              toast.error(tStore("cart.add_failed"), { duration: 6000 });
              return false;
            }
            const result = await updateShopifyCartLine(
              effectiveCartId,
              existingItem.lineId,
              newQuantity,
            );
            if (!restoreCurrent()) return false;
            if (result.success) {
              const currentItems = get().items;
              set({
                items: currentItems.map((i) =>
                  i.lineId === existingItem.lineId ? { ...i, quantity: newQuantity } : i,
                ),
              });
              ok = true;
            } else if (result.cartNotFound) {
              clearCart(recoveryActivity ? initiatingUserId : null, eligibilityGeneration, initiatingIdentityRevision);
              // السلّة انتهت صلاحيّتها في Shopify → ننشئ سلّة جديدة بنفس المنتج
              const fresh = await createShopifyCart({ ...item, lineId: null }, country, cartGift);
              if (!restoreCurrent()) return false;
              if (fresh) {
                set({
                  cartId: fresh.cartId,
                  checkoutUrl: fresh.checkoutUrl,
                  cartCountry: country,
                  items: [{ ...item, lineId: fresh.lineId }],
                });
                ok = true;
              }
            }
          } else {
            const result = await addLineToShopifyCart(effectiveCartId, {
              ...item,
              lineId: null,
            });
            if (!restoreCurrent()) return false;
            if (result.success) {
              const currentItems = get().items;
              set({ items: [...currentItems, { ...item, lineId: result.lineId ?? null }] });
              ok = true;
            } else if (result.cartNotFound) {
              clearCart(recoveryActivity ? initiatingUserId : null, eligibilityGeneration, initiatingIdentityRevision);
              const fresh = await createShopifyCart({ ...item, lineId: null }, country, cartGift);
              if (!restoreCurrent()) return false;
              if (fresh) {
                set({
                  cartId: fresh.cartId,
                  checkoutUrl: fresh.checkoutUrl,
                  cartCountry: country,
                  items: [{ ...item, lineId: fresh.lineId }],
                });
                ok = true;
              }
            }
          }
        } catch (error) {
          console.error("Failed to add item:", error);
        } finally {
          set({ isLoading: false });
          // Optional tracking cannot keep a confirmed cart operation busy.
          const currentItems = get().items;
          const added = currentItems.find((i) => i.variantId === item.variantId);
          if (ok && added) {
            const ga4Payload = {
              id: item.product.node.id,
              title: item.product.node.title,
              price: item.price.amount,
              currency: item.price.currencyCode,
              quantity: item.quantity,
              variantTitle: item.variantTitle,
              tags: item.product.node.tags ?? [],
            };
            const ga4OnlyPayload = {
              ...ga4Payload,
              variantId: added.variantId,
              ...(ga4Market ? { market: ga4Market } : {}),
            };
            quiet(import("@/lib/ga4").then(({ trackAddToCart }) =>
              trackAddToCart(ga4OnlyPayload),
            ));
            quiet(metaLazy(({ trackAddToCart }) =>
              trackAddToCart(ga4Payload),
            ));
            quiet(import("@/lib/tiktokPixel").then(({ trackAddToCart }) =>
              trackAddToCart({
                productId: added.product.node.id,
                variantId: added.variantId,
                title: ga4Payload.title,
                price: ga4Payload.price,
                currency: ga4Payload.currency,
                quantity: ga4Payload.quantity,
              }),
            ));
            quiet(import("@/lib/snapPixel").then(({ trackAddToCart }) =>
                trackAddToCart({
                  variantId: item.variantId,
                  price: item.price.amount,
                  currency: item.price.currencyCode,
                  quantity: item.quantity,
                }),
            ));
            quiet(import("@/lib/googleAdsTag").then(({ trackAddToCart }) =>
              trackAddToCart(ga4Payload),
            ));
            quiet(
              import("@/lib/googleDynamicRemarketing").then(({ trackGoogleRetailEvent }) =>
                trackGoogleRetailEvent(
                  "add_to_cart",
                  {
                    handle: item.product.node.handle,
                    price: item.price.amount,
                    currency: item.price.currencyCode,
                    quantity: item.quantity,
                  },
                  ga4Payload,
                ),
              ),
            );
            quiet(import("@/lib/openaiAdsPixel").then(({ trackAddToCart }) =>
              trackAddToCart(ga4Payload),
            ));
            // 🗄️ تتبّعنا الأوّل-طرفيّ (customer_events) — تعتمد عليه لوحة الزوّار
            // وحملة «سلّة بلا شراء» للمجهولين. لا يُحذف.
            quiet(import("@/lib/activityTracker").then((m) =>
              m.trackAddToCart(
                item.product.node.handle ?? undefined,
                item.product.node.title ?? undefined,
              ),
            ));

            quiet(import("@/lib/funnelClient").then(({ trackFunnelEvent }) => {
              quiet(trackFunnelEvent("checkout", "add_to_cart", {
                productId: item.product.node.id,
                variantId: item.variantId,
              }));
              // قمع وسيط: إضافة للسلّة → بدء الدفع
              quiet(trackFunnelEvent("cart_to_checkout", "add_to_cart", {
                productId: item.product.node.id,
                variantId: item.variantId,
              }));
            }));

            quiet(import("@/lib/visitorDialogTracking").then(({ attributeVisitorDialogConversion }) =>
              attributeVisitorDialogConversion("add_to_cart", {
                productId: item.product.node.id,
                variantId: item.variantId,
              }),
            ));

            // 🎯 نسب «إضافة للسلّة» للسفير إن جاء الزائر من رابطه (كوكي lecmo_ref).
            const ambRef = readAmbassadorRefCookie();
            if (ambRef) {
              void (async () => {
                try {
                  const [{ trackAmbassadorAddToCart }, { getOrCreateSessionId }] = await Promise.all([
                    import("@/utils/ambassadorCart.functions"),
                    import("@/lib/ambassadorTracking"),
                  ]);
                  await trackAmbassadorAddToCart({
                    data: {
                      code: ambRef,
                      sessionId: getOrCreateSessionId(),
                      productHandle: item.product.node.handle ?? null,
                      productTitle: item.product.node.title ?? null,
                    },
                  });
                } catch {
                  /* تتبّع صامت */
                }
              })();
            }
          }
        }
        if (!ok) toast.error(tStore("cart.add_failed"), { duration: 6000 });
        const recoveryAfter = get();
        if (CART_RECOVERY_SOURCE_ENABLED && (initiatingIdentityRevision === getAuthSnapshot().identityRevision && recoveryIdentityMatches(initiatingUserId, getAuthSnapshot().userId)) &&
          ok && recoveryActivity && recoveryAfter.cartId &&
          recoveryFingerprint(recoveryAfter.cartCountry, recoveryAfter.items) !== recoveryBefore) {
          emitRecoveryMutation({ initiatingUserId, eligibilityGeneration, initiatingIdentityRevision, cartId: recoveryAfter.cartId, country: recoveryAfter.cartCountry,
            lines: recoveryAfter.items.map((i) => ({ variantId: i.variantId, quantity: i.quantity })), kind: "mutation" });
        }
        return ok;
      }),

      updateQuantity: async (lineId, quantity) => {
        const initiatingUserId = getAuthSnapshot().userId;
        const eligibilityGeneration = getRecoveryEligibilityGeneration(initiatingUserId);
        const initiatingIdentityRevision = getAuthSnapshot().identityRevision;
        if (quantity <= 0) {
          await get().removeItem(lineId);
          return;
        }
        return withCartLock(async () => {
        const { items, cartId, clearCart } = get();
        const item = items.find((i) => i.lineId === lineId);
        if (!item?.lineId || !cartId) return;
        set({ isLoading: true });
        try {
          const result = await updateShopifyCartLine(cartId, item.lineId, quantity);
          if (result.success) {
            const currentItems = get().items;
            set({
              items: currentItems.map((i) =>
                i.lineId === lineId ? { ...i, quantity } : i,
              ),
            });
            if (item.quantity !== quantity && (initiatingIdentityRevision === getAuthSnapshot().identityRevision && recoveryIdentityMatches(initiatingUserId, getAuthSnapshot().userId))) emitRecoveryMutation({ initiatingUserId, eligibilityGeneration, initiatingIdentityRevision, cartId, country: get().cartCountry,
              lines: get().items.map((i) => ({ variantId: i.variantId, quantity: i.quantity })), kind: "mutation" });
          } else if (result.cartNotFound) {
            clearCart(initiatingUserId, eligibilityGeneration, initiatingIdentityRevision);
          }
        } catch (error) {
          console.error("Failed to update quantity:", error);
        } finally {
          set({ isLoading: false });
        }
        });
      },

      removeItem: (lineId) => withRecoveryMutationLock(async (initiatingUserId, eligibilityGeneration, initiatingIdentityRevision) => {
        const { items, cartId, clearCart, cartCountry } = get();
        // GA4-only: country captured before the mutation/clearCart.
        const ga4Market = cartCountry ?? undefined;
        const item = items.find((i) => i.lineId === lineId);
        if (!item?.lineId || !cartId) return;
        set({ isLoading: true });
        try {
          const result = await removeLineFromShopifyCart(cartId, item.lineId);
          if (result.success) {
            const ga4RemovePayload = {
              id: item.product.node.id,
              title: item.product.node.title,
              price: item.price.amount,
              currency: item.price.currencyCode,
              quantity: item.quantity,
              variantId: item.variantId,
              ...(ga4Market ? { market: ga4Market } : {}),
            };
            const currentItems = get().items;
            const newItems = currentItems.filter((i) => i.lineId !== lineId);
            if (newItems.length === 0) {
              clearCart(initiatingUserId, eligibilityGeneration, initiatingIdentityRevision);
            } else {
              set({ items: newItems });
              if ((initiatingIdentityRevision === getAuthSnapshot().identityRevision && recoveryIdentityMatches(initiatingUserId, getAuthSnapshot().userId))) emitRecoveryMutation({ initiatingUserId, eligibilityGeneration, initiatingIdentityRevision, cartId, country: cartCountry,
                lines: newItems.map((i) => ({ variantId: i.variantId, quantity: i.quantity })), kind: "mutation" });
            }
            // Emit only once Shopify confirms the removal. A rejected mutation
            // leaves both the cart and its analytics unchanged.
            quiet(import("@/lib/ga4").then(({ trackRemoveFromCart }) =>
              trackRemoveFromCart(ga4RemovePayload),
            ));
            quiet(import("@/lib/activityTracker").then((m) =>
              m.trackRemoveFromCart(
                item.product.node.handle ?? undefined,
                item.product.node.title ?? undefined,
              ),
            ));
          } else if (result.cartNotFound) {
            clearCart(initiatingUserId, eligibilityGeneration, initiatingIdentityRevision);
          }
        } catch (error) {
          console.error("Failed to remove item:", error);
        } finally {
          set({ isLoading: false });
        }
      }),

      setCartGift: async (gift) => {
        const seq = ++giftIntentSeq;
        // المسودّة تُحفظ محلّياً للتحرير، لكن النجاح يُعلن فقط بعد تأكيد Shopify،
        // والدفع يعيد التحقّق منها على السلّة الحاليّة.
        set({ cartGift: gift });
        return withCartLock(async () => {
          // نيّة أحدث (حفظ/إزالة) ستكتب بعدنا — لا نكتب نسخة قديمة.
          if (seq !== giftIntentSeq) return false;
          const { cartId, cartCountry, cartMarketUncertain } = get();
          if (!cartId || cartMarketUncertain) return false;
          if (!isGiftContactComplete(gift)) return false;
          // الوجهة يجب أن تطابق سوق السلّة الفعليّ (التبديل يمرّ بالتأكيد فقط).
          if (gift.recipientAddress?.country !== cartCountry) return false;
          const ok = await syncCartGiftAttribute(cartId, gift);
          if (seq !== giftIntentSeq || get().cartId !== cartId || get().cartGift !== gift) return false;
          if (!ok) return false;
          // تعبئة العنوان في checkout تحسين فقط — لا يُبنى عليه نجاح.
          await syncRecipientAddressToBuyerIdentity(cartId, gift);
          return seq === giftIntentSeq && get().cartId === cartId;
        });
      },

      clearCartGift: async () => {
        const seq = ++giftIntentSeq;
        const previous = get().cartGift;
        set({ cartGift: null });
        return withCartLock(async () => {
          if (seq !== giftIntentSeq) return false;
          const { cartId } = get();
          if (!cartId) return true;
          const ok = await syncCartGiftAttribute(cartId, null);
          if (seq !== giftIntentSeq || get().cartId !== cartId) return false;
          if (!ok) {
            // لم يُؤكَّد المحو على Shopify — نُبقي الهديّة ظاهرة بدل ادّعاء الإزالة.
            set({ cartGift: previous });
            return false;
          }
          return true;
        });
      },

      switchCartMarketForGift: (target, opts) => withCartLock(async (): Promise<MarketSwitchResult> => {
        const { cartId, cartCountry, items, discountCode, cartMarketUncertain } = get();
        if (!cartId || !cartCountry || items.length === 0) return { ok: true };
        if (cartCountry === target && !cartMarketUncertain) return { ok: true };
        if (cartMarketUncertain) return { ok: false, reason: "uncertain" };
        // Returning to the shopper's own shippable market (e.g. KW/BH) is allowed even
        // though it is not an active gift destination.
        const shopperReturn = !!opts?.returnToShopperMarket &&
          canCheckoutFromMarket(marketFromCountryCode(target));
        if (!shopperReturn && !isGiftDestinationActive(target)) return { ok: false, reason: "unavailable" };
        const expectedCurrency = MARKETS[marketFromCountryCode(target)].currency;
        const sig = linesSignature(items);
        const changed = () => get().cartId !== cartId || linesSignature(get().items) !== sig ||
          get().cartCountry !== cartCountry;
        const adopt = (cart: any, country: CountryCode) => {
          const lines = new Map<string, any>((cart.lines.edges ?? []).map((e: any) => [e.node.id, e.node]));
          set({
            cartCountry: country,
            cartMarketUncertain: false,
            checkoutUrl: formatCheckoutUrl(cart.checkoutUrl, discountCode),
            items: get().items.map((i) => {
              const cost = lines.get(i.lineId!)!.cost.amountPerQuantity;
              return {
                ...i,
                price: i.isSampleGift
                  ? { amount: i.price.amount, currencyCode: cost.currencyCode }
                  : { amount: String(cost.amount), currencyCode: cost.currencyCode },
              };
            }),
          });
          lastSyncSignature = null;
        };
        set({ isLoading: true });
        try {
          // 1) توفّر وأسعار الوجهة من Shopify قبل أيّ تغيير (لا شيء تغيّر إن فشل).
          let nodes: any[] | null = null;
          let resolvedCountry: string | undefined;
          try {
            const v = await storefrontApiRequest<any>(
              VARIANTS_IN_CONTEXT_QUERY,
              { ids: [...new Set(items.map((i) => i.variantId))], country: target, language: checkoutLanguage() },
              { cacheTtlMs: 0 },
            );
            nodes = Array.isArray(v?.data?.nodes) ? v.data.nodes : null;
            resolvedCountry = v?.data?.localization?.country?.isoCode;
          } catch {
            nodes = null;
          }
          if (changed()) return { ok: false, reason: "stale" };
          if (!nodes) return { ok: false, reason: "failed" };
          const byId = new Map(nodes.filter(Boolean).map((n: any) => [n.id, n]));
          const missing = items.filter((i) => {
            const n: any = byId.get(i.variantId);
            return !n || !n.availableForSale || (isAddedShippingCountry(target)
              ? !matchesAddedCountryPricing(target, resolvedCountry, n.price?.currencyCode)
              : n.price?.currencyCode !== expectedCurrency);
          });
          if (missing.length) {
            return {
              ok: false,
              reason: "unavailable",
              titles: missing.map((i) => i.sampleDisplayName ?? i.product.node.title),
            };
          }
          // 2) نقل سوق السلّة (Shopify يعيد التسعير بأسعار التاجر للوجهة).
          //    نتيجة الطلب وحدها لا تكفي — المصدر هو القراءة الراجعة.
          try {
            await storefrontApiRequest<any>(
              CART_BUYER_IDENTITY_UPDATE_MUTATION,
              { cartId, buyerIdentity: { countryCode: target } },
              { cacheTtlMs: 0 },
            );
          } catch (error) {
            console.error("Gift market switch request failed:", error);
          }
          const moved = await readCartInContext(cartId, target);
          if (!changed() && cartCoherentWith(moved, cartId, items, target)) {
            adopt(moved, target);
            return { ok: true };
          }
          // 3) إرجاع وتحقّق: لا نقول «لم تتغيّر سلّتك» إلّا بعد قراءة تثبت ذلك.
          try {
            await storefrontApiRequest<any>(
              CART_BUYER_IDENTITY_UPDATE_MUTATION,
              { cartId, buyerIdentity: { countryCode: cartCountry } },
              { cacheTtlMs: 0 },
            );
          } catch (error) {
            console.error("Market switch revert failed:", error);
          }
          const back = await readCartInContext(cartId, cartCountry);
          if (get().cartId === cartId && cartCoherentWith(back, cartId, get().items, cartCountry)) {
            adopt(back, cartCountry);
            return { ok: false, reason: "failed" };
          }
          if (get().cartId === cartId) set({ cartMarketUncertain: true });
          return { ok: false, reason: "uncertain" };
        } finally {
          set({ isLoading: false });
        }
      }),

      recoverCartMarket: () => withCartLock(async () => {
        const { cartId, cartCountry, items, cartGift } = get();
        const identity = captureCheckoutIdentity(getAuthSnapshot);
        if (!cartId || !cartCountry) {
          set({ cartMarketUncertain: false });
          return true;
        }
        set({ isLoading: true });
        try {
          const probe = await readCartInContext(cartId, cartCountry);
          const actual = probe?.buyerIdentity?.countryCode as CountryCode | undefined;
          if (!identity.matches() || get().cartGift !== cartGift) return false;
          if (isAddedShippingCountry(cartCountry) || cartCountry === "US") {
            // A matching contextual price is not proof that Shopify's persisted
            // checkout address was reset. Keep the old checkout quarantined;
            // explicit emptying/new selection is the safe path, not URL reuse.
            if (get().checkoutContextBlocked || get().cartMarketUncertain || actual !== cartCountry) {
              set({ checkoutContextBlocked: true, cartMarketUncertain: true, checkoutUrl: null,
                contextualSubtotal: null, shippingQuote: null });
              return false;
            }
            // Explicit recovery restores the selected destination, never silently
            // adopts an address edited in Shopify checkout (especially a gift).
            if (!actual || probe?.resolvedCountry !== cartCountry ||
              (cartGift && cartGift.recipientAddress?.country !== cartCountry)) return false;
            const verified = probe;
            if (!identity.matches() || get().cartId !== cartId || get().cartGift !== cartGift ||
              linesSignature(get().items) !== linesSignature(items)) return false;
            const reconciled = reconcileContextCart(verified, cartId, cartCountry, items, cartGift?.recipientAddress?.country);
            if (!reconciled) return false;
            set({ items: reconciled.items, contextualMoneyItems: reconciled.items, contextualSubtotal: reconciled.subtotal, shippingQuote: reconciled.shipping,
              checkoutUrl: formatCheckoutUrl(verified.checkoutUrl, get().discountCode), cartMarketUncertain: false });
            lastSyncSignature = null;
            return true;
          }
          if (!actual || (!isGiftDestinationActive(actual) && actual !== cartCountry)) return false;
          if (cartGift && cartGift.recipientAddress?.country !== actual) return false;
          const cart = actual === cartCountry ? probe : await readCartInContext(cartId, actual);
          if (!identity.matches() || get().cartGift !== cartGift || get().cartId !== cartId || linesSignature(get().items) !== linesSignature(items)) return false;
          if (!cartCoherentWith(cart, cartId, items, actual)) return false;
          const lines = new Map<string, any>((cart.lines.edges ?? []).map((e: any) => [e.node.id, e.node]));
          set({
            cartCountry: actual,
            cartMarketUncertain: false,
            checkoutUrl: formatCheckoutUrl(cart.checkoutUrl, get().discountCode),
            items: get().items.map((i) => {
              const cost = lines.get(i.lineId!)!.cost.amountPerQuantity;
              return {
                ...i,
                price: i.isSampleGift
                  ? { amount: i.price.amount, currencyCode: cost.currencyCode }
                  : { amount: String(cost.amount), currencyCode: cost.currencyCode },
              };
            }),
          });
          lastSyncSignature = null;
          return true;
        } finally {
          set({ isLoading: false });
        }
      }),

      applyDiscountCode: (code) => withCartLock(async () => {
        const { cartId, clearCart } = get();
        if (!cartId) {
          toast.error(tStore("cart.discount_no_cart"));
          return;
        }
        const trimmed = code.trim();
        if (!trimmed) return;
        set({ isLoading: true });
        try {
          const data = await storefrontApiRequest<any>(CART_DISCOUNT_CODES_UPDATE_MUTATION, {
            cartId,
            discountCodes: [trimmed],
          }, { cacheTtlMs: 0 });
          const userErrors = data?.data?.cartDiscountCodesUpdate?.userErrors || [];
          if (isCartNotFoundError(userErrors)) {
            clearCart();
            return;
          }
          if (userErrors.length) {
            console.error("Discount code apply failed:", userErrors);
            toast.error(tStore("cart.discount_apply_error"));
            return;
          }
          set({ discountCode: trimmed });
          toast.success(tStore("cart.discount_applied", { code: trimmed }));
        } catch (error) {
          console.error("Failed to apply discount code:", error);
          toast.error(tStore("cart.discount_apply_error"));
        } finally {
          set({ isLoading: false });
        }
      }),

      clearDiscountCode: () => {
        const { cartId } = get();
        set({ discountCode: null });
        if (cartId) {
          storefrontApiRequest<any>(CART_DISCOUNT_CODES_UPDATE_MUTATION, {
            cartId,
            discountCodes: [],
          }, { cacheTtlMs: 0 }).catch((e) => console.error("Failed to clear discount code:", e));
        }
      },

      addSampleGift: ({ programId, sampleId, shopifyVariantId, displayName, imageUrl, concentration, sizeMl, userId }) => withCartLock(async () => {
        const { cartId, cartCountry, items } = get();
        if (!cartId || !cartCountry) {
          // العيّنة لا تُنشئ سلّة بمفردها — تتطلّب منتج مدفوع موجود مسبقاً
          return;
        }
        // line attributes للسطر — ليتعرّف عليه webhook
        // المفاتيح التي تبدأ بـ `_` تبقى مخفيّة عن العميل في صفحة الـ checkout.
        const attrs: Array<{ key: string; value: string }> = [
          { key: "is_sample_gift", value: "true" },
          { key: "Gift program ID", value: programId },
          { key: "Gift sample ID", value: sampleId },
          { key: "Sample name", value: displayName },
        ];
        if (concentration) attrs.push({ key: "Concentration", value: concentration });
        if (sizeMl) attrs.push({ key: "Size", value: `${sizeMl}ml` });
        if (userId) attrs.push({ key: "_gift_user_id", value: userId });
        set({ isLoading: true });
        try {
          const data = await storefrontApiRequest<any>(CART_LINES_ADD_MUTATION, {
            cartId,
            lines: [{ quantity: 1, merchandiseId: shopifyVariantId, attributes: attrs }],
          }, { cacheTtlMs: 0 });
          const payload = data?.data?.cartLinesAdd;
          const userErrors = payload?.userErrors || [];
          if (!payload || userErrors.length) {
            console.error("Add sample failed:", userErrors);
            return;
          }
          // تأكيد السلّة والسطر نفسيهما؛ لا عيّنة محلّيّة وهميّة من ردّ ناقص/لسلّة أخرى.
          if (payload.cart?.id !== cartId || get().cartId !== cartId) {
            console.error("Add sample response was not for the current cart");
            return;
          }
          const lines = payload.cart?.lines?.edges || [];
          const newLine = lines.find((l: any) => l?.node?.merchandise?.id === shopifyVariantId);
          const lineId = newLine?.node?.id ?? null;
          if (!lineId) {
            console.error("Add sample response omitted the new line");
            return;
          }

          // نبني CartItem اصطناعيّ — سعر 0، مع علم isSampleGift
          const sampleItem: CartItem = {
            lineId,
            // product وهميّ مبسّط — لا يُعرض في UI الاعتيادي (نعرضه عبر sampleDisplayName)
            product: {
              node: {
                id: `sample::${sampleId}`,
                title: displayName,
                description: "",
                handle: "",
                priceRange: { minVariantPrice: { amount: "0", currencyCode: items[0]?.price.currencyCode ?? "USD" } },
                images: { edges: imageUrl ? [{ node: { url: imageUrl, altText: displayName } }] : [] },
                variants: { edges: [] },
                options: [],
              },
            } as any,
            variantId: shopifyVariantId,
            variantTitle: displayName,
            price: { amount: "0", currencyCode: items[0]?.price.currencyCode ?? "USD" },
            quantity: 1,
            selectedOptions: [],
            isSampleGift: true,
            programId,
            sampleId,
            sampleDisplayName: displayName,
            sampleImageUrl: imageUrl,
            sampleConcentration: concentration ?? null,
            sampleSizeMl: sizeMl ?? null,
          };
          set({ items: [...get().items, sampleItem] });
        } catch (error) {
          console.error("Failed to add sample gift:", error);
        } finally {
          set({ isLoading: false });
        }
      }),

      clearCart: (initiatingUserId = getAuthSnapshot().userId, eligibilityGeneration = getRecoveryEligibilityGeneration(initiatingUserId), initiatingIdentityRevision = getAuthSnapshot().identityRevision) => {
        const recoveryCart = get();
        if (recoveryCart.cartId && (initiatingIdentityRevision === getAuthSnapshot().identityRevision && recoveryIdentityMatches(initiatingUserId, getAuthSnapshot().userId))) emitRecoveryMutation({ initiatingUserId, eligibilityGeneration, initiatingIdentityRevision, cartId: recoveryCart.cartId,
          country: recoveryCart.cartCountry, lines: [], kind: "empty" });
        lastSyncSignature = null;
        lastSyncAt = 0;
        set({ items: [], contextualSubtotal: null, shippingQuote: null, cartId: null, checkoutUrl: null, cartCountry: null, cartGift: null, discountCode: null, checkoutStartedAt: null, cartMarketUncertain: false, checkoutContextBlocked: false });
      },


      getCheckoutUrl: () => get().checkoutUrl,

      prepareCheckout: () => {
        const initiatingIdentity = captureCheckoutIdentity(getAuthSnapshot);
        return withCartLock(async () => {
        if (!initiatingIdentity.matches()) {
          toast.error(tStore("cart.checkout_changed"));
          return null;
        }
        const { cartId, cartCountry, items, clearCart, discountCode, cartMarketUncertain } = get();
        if (!cartId || !cartCountry || items.length === 0) return null;
        if (!canCheckoutFromMarket(marketFromCountryCode(cartCountry))) {
          toast.error(tStore("cart.row_no_shipping"));
          return null;
        }
        if (cartMarketUncertain || get().checkoutContextBlocked) {
          toast.error(tStore("cart.market_uncertain"), { duration: 8000 });
          return null;
        }
        // لقطة متّسقة: أيّ تغيير في السلّة/السوق/الهديّة أثناء التجهيز يُلغي هذا الدفع.
        const giftSnapshot = get().cartGift;
        const sig = linesSignature(items);
        const fresh = () => {
          const s = get();
          return initiatingIdentity.matches() && s.cartId === cartId && s.cartCountry === cartCountry && s.cartGift === giftSnapshot &&
            linesSignature(s.items) === sig && !s.cartMarketUncertain && !s.checkoutContextBlocked;
        };
        const notFresh = () => {
          toast.error(tStore(giftSnapshot ? "gift.checkout_not_ready" : "cart.checkout_changed"));
          return null;
        };

        set({ isSyncing: true });
        try {
          if (isAddedShippingCountry(cartCountry) || cartCountry === "US") {
            const priced = await readCartInContext(cartId, cartCountry);
            if (!fresh()) return notFresh();
            const reconciled = reconcileContextCart(priced, cartId, cartCountry, items, giftSnapshot?.recipientAddress?.country);
            if (!reconciled) {
              set({ cartMarketUncertain: true, checkoutContextBlocked: true, checkoutUrl: null, contextualSubtotal: null, shippingQuote: null });
              toast.error(tStore("cart.destination_unavailable"));
              return null;
            }
            set({ items: reconciled.items, contextualMoneyItems: reconciled.items, contextualSubtotal: reconciled.subtotal, shippingQuote: reconciled.shipping });
          }
          const identityData = await storefrontApiRequest<any>(
            CART_BUYER_IDENTITY_UPDATE_MUTATION,
            { cartId, buyerIdentity: { countryCode: cartCountry } },
            { cacheTtlMs: 0 },
          );
          const identityErrors =
            identityData?.data?.cartBuyerIdentityUpdate?.userErrors ?? [];
          initiatingIdentity.assertCurrent();
          if (!identityData?.data?.cartBuyerIdentityUpdate || identityErrors.length > 0 ||
            identityData.data.cartBuyerIdentityUpdate.cart?.id !== cartId) {
            throw new Error("Checkout market update was not confirmed");
          }

          const data = await storefrontApiRequest<any>(
            CART_CHECKOUT_QUERY,
            { id: cartId, country: cartCountry, language: checkoutLanguage() },
            { cacheTtlMs: 0 },
          );
          const cart = data?.data?.cart;
          if (!fresh()) return notFresh();
          if (cart === null || cart?.totalQuantity === 0) {
            clearCart();
            return null;
          }
          if (!cart?.checkoutUrl) throw new Error("Checkout response was incomplete");
          if (cart.id !== cartId) throw new Error("Checkout response was for another cart");
          if (isAddedShippingCountry(cartCountry) && (
            !matchesAddedCountryPricing(cartCountry, data?.data?.localization?.country?.isoCode, cart.cost?.subtotalAmount?.currencyCode) ||
            cart.buyerIdentity?.countryCode !== cartCountry
          )) {
            toast.error(tStore("cart.destination_unavailable"));
            return null;
          }

          // A cart may predate the ambassador link, and its Google session /
          // click / UTM attributes may predate later visits or a consent change.
          // Refresh both claims at the handoff, retaining all existing
          // gift/visitor/custom attributes. An expired/invalid cookie must not
          // leave an older ref on Shopify, and revoked Google consent must
          // remove the attributes we own.
          // الهديّة: تحقّق من اكتمال الأرقام والسوق، ومن أنّ آخر سمات محفوظة على
          // هذه السلّة تطابق الهديّة الحاليّة (حتى بعد إعادة التحميل). fail closed.
          const gift = giftSnapshot;
          const giftExpected = await expectedGiftAttributes(gift);
          initiatingIdentity.assertCurrent();
          if (gift && (!isGiftContactComplete(gift) || gift.recipientAddress?.country !== cartCountry)) {
            toast.error(tStore("gift.checkout_not_ready"));
            return null;
          }
          if (!giftAttributesMatch((cart.attributes ?? []) as Attr[], giftExpected)) {
            const synced = await syncCartGiftAttribute(cartId, gift);
            initiatingIdentity.assertCurrent();
            const readback = synced
              ? await storefrontApiRequest<any>(CART_ATTRIBUTES_QUERY, { id: cartId }, { cacheTtlMs: 0 })
              : null;
            initiatingIdentity.assertCurrent();
            const freshAttrs = (readback?.data?.cart?.id === cartId
              ? readback?.data?.cart?.attributes ?? null
              : null) as Attr[] | null;
            if (!freshAttrs || !giftAttributesMatch(freshAttrs, giftExpected)) {
              toast.error(tStore("gift.checkout_not_ready"));
              return null;
            }
            cart.attributes = freshAttrs;
          }
          if (!fresh()) return notFresh();


          const ref = readAmbassadorRefCookie() ?? "";
          const existingAttrs = (cart.attributes ?? []) as Array<{ key: string; value: string }>;
          const previousRef = existingAttrs.find((attr) => attr.key === "ambassador_ref")?.value ?? "";
          const refChanged = previousRef !== ref;

          // Analytics is optional: a capture failure yields fewer attributes and
          // must never block checkout.
          // Meta-owned click attributes follow current ads consent too: they are
          // refreshed (or removed after withdrawal) on this same required write.
          // Removing them here does not revoke an already-sent server Purchase.
          const freshMeta = safeBuildMeta(buildMetaClickAttributes);
          // Snap-owned click attributes follow the same consent bridge:
          // refreshed (or removed after withdrawal) on this same write.
          const freshSnap = safeBuildMeta(buildSnapClickAttributes);
          const freshGoogle = [...buildGoogleCartAttributes(), ...freshMeta, ...freshSnap];
          const isTrackingOwned = (key: string) =>
            GOOGLE_OWNED_CART_ATTR_KEYS.includes(key) || META_CLICK_ATTR_KEYS.includes(key) || SNAP_CLICK_ATTR_KEYS.includes(key);
          const serialize = (attrs: Array<{ key: string; value: string }>) =>
            attrs
              .map((a) => `${a.key}=${a.value}`)
              .sort()
              .join("|");
          const previousGoogle = existingAttrs.filter((attr) => isTrackingOwned(attr.key));
          // `_ga_captured_at` moves on every capture, so compare the identity
          // fields only — otherwise every handoff would write to Shopify.
          const identity = (attrs: Array<{ key: string; value: string }>) =>
            attrs.filter((a) => a.key !== "_ga_captured_at");
          const googleChanged =
            serialize(identity(previousGoogle)) !== serialize(identity(freshGoogle));

          // The same consent bridge serves ordinary and gift carts. It only
          // changes the OpenAI pointer; gift attributes were verified above.
          let openAiAttrs = existingAttrs;
          let bridgeTimeout: ReturnType<typeof setTimeout> | undefined;
          try {
            openAiAttrs = await Promise.race([
              prepareOpenAiCartAttributes(existingAttrs),
              new Promise<typeof existingAttrs>((resolve) => {
                bridgeTimeout = setTimeout(() => resolve(existingAttrs), 1500);
              }),
            ]);
          } catch {
            // Optional measurement must not block checkout.
          } finally {
            clearTimeout(bridgeTimeout);
          }
          if (!fresh()) return notFresh();
          const checkoutAttributes = (attrs: typeof existingAttrs) => {
            return mergeCheckoutTrackingAttributes(attrs, freshGoogle, ref, previousRef);
          };
          // Optional OpenAI data must not turn a valid baseline mutation into
          // a Shopify limit error. Never evict gift/referral/other tracking data.
          if (serialize(openAiAttrs) !== serialize(existingAttrs) &&
              checkoutAttributes(openAiAttrs).length > 250) openAiAttrs = existingAttrs;
          // Never add an independent Shopify write for tracking. Full-list
          // mutations cannot be safely abandoned while newer cart edits proceed.
          // Carry the pointer only on an update already required by checkout.
          if (refChanged || googleChanged) {
            let timeout: ReturnType<typeof setTimeout> | undefined;
            try {
              const attributes = checkoutAttributes(openAiAttrs);

              // Bound the checkout wait without changing the shared Shopify
              // client. The request can still finish after this deadline; the
              // race observes its rejection and cannot hand off checkout late.
              const request = storefrontApiRequest<any>(CART_ATTRIBUTES_UPDATE_MUTATION, { cartId, attributes }, { cacheTtlMs: 0 });
              // إن تجاوز الطلب المهلة يبقى قفل السلّة مُمسَكاً حتى يستقرّ، فلا
              // يهبط متأخّراً فوق حفظ/إزالة هديّة أحدث.
              holdCartLockUntil(request);
              const updated = await Promise.race([
                request,
                new Promise<never>((_resolve, reject) => {
                  timeout = setTimeout(() => reject(new Error("Checkout attributes update timed out")), 10_000);
                }),
              ]);
              const payload = updated?.data?.cartAttributesUpdate;
              initiatingIdentity.assertCurrent();
              const errors = payload?.userErrors ?? [];
              if (errors.length || payload?.cart?.id !== cartId) {
                throw new Error("Shopify did not confirm checkout attributes");
              }
              // الكتابة نفسها تحمل سمات الهديّة المتحقَّق منها — نتأكّد أنّها بقيت.
              const savedAttrs = payload.cart.attributes as Attr[] | undefined;
              if (gift && (!Array.isArray(savedAttrs) || !giftAttributesMatch(savedAttrs, giftExpected))) {
                toast.error(tStore("gift.checkout_not_ready"));
                return null;
              }
            } catch (error) {
              initiatingIdentity.assertCurrent();
              console.error("Checkout attributes update failed:", error);
              // Preserve the existing required-write failure policy and lock.
              if (gift) {
                toast.error(tStore("gift.checkout_not_ready"));
                return null;
              }
              if (refChanged) {
                // Keep the existing cart for retry rather than knowingly handing
                // off checkout with an incorrect ambassador claim.
                toast.error(getActiveLocale() === "ar"
                  ? "تعذّر تجهيز الدفع. حاول مرّة أخرى."
                  : "Couldn't prepare checkout. Please try again.");
                return null;
              }
              // Analytics-only refresh failed: proceed to checkout.
            } finally {
              clearTimeout(timeout);
            }
          }


          if (isAddedShippingCountry(cartCountry) || cartCountry === "US") {
            const finalCart = await readCartInContext(cartId, cartCountry);
            if (!fresh()) return notFresh();
            const reconciled = reconcileContextCart(finalCart, cartId, cartCountry, get().items, giftSnapshot?.recipientAddress?.country);
            if (!reconciled) {
              set({ cartMarketUncertain: true, checkoutContextBlocked: true, checkoutUrl: null, contextualSubtotal: null, shippingQuote: null });
              return notFresh();
            }
            set({ items: reconciled.items, contextualMoneyItems: reconciled.items, contextualSubtotal: reconciled.subtotal, shippingQuote: reconciled.shipping });
            cart.checkoutUrl = finalCart.checkoutUrl;
          }

          if (!fresh()) return notFresh();
          const freshUrl = formatCheckoutUrl(cart.checkoutUrl, discountCode);
          if (CART_RECOVERY_SOURCE_ENABLED) {
            if (!(await cancelRecoveryBeforeCheckout(cartId, initiatingIdentity))) throw new Error("Recovery handoff acknowledgement failed");
            if (!fresh()) return notFresh();
          }
          initiatingIdentity.assertCurrent();
          set({ checkoutUrl: freshUrl, checkoutStartedAt: Date.now() });
          initiatingIdentity.assertCurrent();
          return freshUrl;

        } catch (error) {
          console.error("Failed to prepare Shopify checkout:", error);
          toast.error(getActiveLocale() === "ar"
            ? "تعذّر تجهيز الدفع. بقيت المنتجات في سلّتك، حاول مرّة أخرى."
            : "Couldn’t prepare checkout. Your items are still in your bag; please try again.");
          return null;
        } finally {
          set({ isSyncing: false });
        }
        });
      },

      syncCart: async ({ force = false } = {}) => {
        const { cartId, isSyncing, isLoading, clearCart, items, cartCountry, cartGift } = get();
        const identity = captureCheckoutIdentity(getAuthSnapshot);
        if (!cartId || isSyncing || isLoading) return;

        // بوّابة التغيّر الفعليّ: لا نُزامن عند مجرّد العودة للتبويب/فتح الدرج
        // إن لم تتغيّر بصمة السلّة ولم تنتهِ نافذة الفاصل الزمنيّ.
        const signature = cartSignature({ cartId, cartCountry, items });
        const unchanged = signature === lastSyncSignature;
        const recent = Date.now() - lastSyncAt < SYNC_MIN_INTERVAL_MS;
        if (!force && unchanged && recent) return;
        lastSyncSignature = signature;
        lastSyncAt = Date.now();

        set({ isSyncing: true });
        let failed = false;
        try {
          const data = await storefrontApiRequest<any>(CART_QUERY, { id: cartId }, { cacheTtlMs: 0 });
          // A late response from another cart must not erase a newer cart.
          if (!identity.matches() || get().cartGift !== cartGift || get().cartCountry !== cartCountry || get().cartId !== cartId || get().items !== items || get().isLoading) {
            failed = true;
            return;
          }
          const cart = data?.data?.cart;
          if (cart === undefined) throw new Error("Cart sync response was incomplete");
          if (cart === null || cart.totalQuantity === 0) {
            // سلّة Shopify هي مصدر الحقيقة: فارغة/مفقودة = طلب مكتمل أو سلّة
            // منتهية → نمسح دائماً. ممنوع إعادة بناء سلّة من عناصر محفوظة
            // محلّياً (كانت تُعيد منتجات سبق شراؤها بعد ٦ ساعات).
            clearCart();
            return;
          }
          if (cartCountry && (isAddedShippingCountry(cartCountry) || cartCountry === "US")) {
            if (get().checkoutContextBlocked || get().cartMarketUncertain) {
              set({ checkoutContextBlocked: true, cartMarketUncertain: true, checkoutUrl: null });
              failed = true;
              return;
            }
            const priced = await readCartInContext(cartId, cartCountry);
            if (!identity.matches() || get().cartId !== cartId || get().items !== items ||
              get().cartCountry !== cartCountry || get().cartGift !== cartGift || get().isLoading) { failed = true; return; }
            const reconciled = reconcileContextCart(priced, cartId, cartCountry, items, cartGift?.recipientAddress?.country);
            if (!reconciled) {
              set({ cartMarketUncertain: true, checkoutContextBlocked: true, checkoutUrl: null, contextualSubtotal: null, shippingQuote: null });
              failed = true;
              return;
            }
            set({ items: reconciled.items, contextualMoneyItems: reconciled.items, contextualSubtotal: reconciled.subtotal, shippingQuote: reconciled.shipping,
              checkoutUrl: formatCheckoutUrl(priced.checkoutUrl, get().discountCode), cartMarketUncertain: false });
          }
          // الصور المحفوظة في localStorage قد تكون قديمة بعد تحديثها في Shopify.
          // نُحدّثها من المصدر الحيّ لكلّ منتج في السلّة.
          const country = (get().cartCountry ?? "AE") as CountryCode;
          const handles = Array.from(
            new Set(get().items.map((i) => i.product?.node?.handle).filter(Boolean) as string[]),
          );
          if (handles.length > 0) {
            const fresh = await Promise.all(
              handles.map(async (h) => [h, await fetchProductByHandle(h, country)] as const),
            );
            if (!identity.matches() || get().cartId !== cartId || get().cartCountry !== cartCountry ||
              get().cartGift !== cartGift || linesSignature(get().items) !== linesSignature(items) || get().isLoading) { failed = true; return; }
            const imagesByHandle = new Map(
              fresh
                .filter(([, node]) => !!node?.images?.edges?.length)
                .map(([h, node]) => [h, node!.images]),
            );
            if (imagesByHandle.size > 0) {
              const previousItems = get().items;
              const refreshedItems = previousItems.map((it) => {
                const images = imagesByHandle.get(it.product?.node?.handle ?? "");
                return images ? { ...it, product: { ...it.product, node: { ...it.product.node, images } } } : it;
              });
              set({
                items: refreshedItems,
                contextualMoneyItems: get().contextualMoneyItems === previousItems ? refreshedItems : null,
              });
            }
          }
        } catch (error) {
          console.error("Failed to sync cart with Shopify:", error);
          failed = true;
        } finally {
          if (failed) {
            // فشل المزامنة لا يُحتسب مزامنة ناجحة — نسمح بمحاولة تالية فوراً.
            lastSyncSignature = null;
            lastSyncAt = 0;
          } else {
            // نحدّث البصمة بحالة السلّة بعد المزامنة (قد تكون أُعيد بناؤها).
            const s = get();
            lastSyncSignature = cartSignature({
              cartId: s.cartId,
              cartCountry: s.cartCountry,
              items: s.items,
            });
          }
          set({ isSyncing: false });
        }
      },
    }),
    {
      name: "shopify-cart-v3",
      storage: createJSONStorage(() => safeStorage("local")),
      partialize: (state) => ({
        items: state.items,
        cartId: state.cartId,
        checkoutUrl: state.checkoutUrl,
        cartCountry: state.cartCountry,
        cartGift: state.cartGift,
        discountCode: state.discountCode,
        checkoutStartedAt: state.checkoutStartedAt,
        cartMarketUncertain: state.cartMarketUncertain,
        checkoutContextBlocked: state.checkoutContextBlocked,
      }),

    },
  ),
);
